import {useQuery} from '@tanstack/react-query';
import {DocumentRedactionResponse, DocumentService} from 'client';
import {COMMAND_PRIORITY_LOW, createCommand} from 'lexical';
import {createContext, ReactNode, useContext, useMemo} from 'react';
import {getService} from 'utilities';

export type RedactionV2EditorPayload = {
  nodeKey: string;
  redactionId?: string;
};

export const TOGGLE_REDACTION_V2_EDITOR_COMMAND = createCommand<RedactionV2EditorPayload>();

type RedactionV2ContextValue = {
  documentId?: string;
  enabled: boolean;
};

const RedactionV2Context = createContext<RedactionV2ContextValue>({
  documentId: undefined,
  enabled: false,
});

export const REDACTION_V2_COMMAND_PRIORITY = COMMAND_PRIORITY_LOW;

export const redactionV2EntryQueryKey = (documentId?: string, redactionId?: string) => [
  'document',
  'redactions',
  'v2',
  documentId,
  redactionId,
];

export const RedactionV2Provider = ({
  documentId,
  enabled,
  children,
}: {
  documentId?: string;
  enabled: boolean;
  children: ReactNode;
}) => {
  const value = useMemo(
    () => ({
      documentId,
      enabled: Boolean(enabled && documentId),
    }),
    [documentId, enabled]
  );

  return <RedactionV2Context.Provider value={value}>{children}</RedactionV2Context.Provider>;
};

export const useRedactionV2Context = () => useContext(RedactionV2Context);

export const useRedactionV2Entry = (redactionId: string) => {
  const {documentId, enabled} = useRedactionV2Context();

  return useQuery<DocumentRedactionResponse | null>({
    queryKey: redactionV2EntryQueryKey(documentId, redactionId),
    queryFn: async () => {
      const entries = await getService(
        DocumentService
      ).resolveRedactionsApiV1DocumentDocumentIdRedactionsResolvePost(documentId!, {
        redaction_ids: [redactionId],
      });
      return entries[0] ?? null;
    },
    enabled: Boolean(enabled && documentId && redactionId),
    staleTime: 30_000,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: true,
  });
};
